import React, { Dispatch, SetStateAction, useContext, useState } from 'react';
import tableTools from '../helpers/tableTools.ts';
import { Button, MantineBreakpoint, Stack, Table } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { theme } from '../style/theme.tsx';
import classes from '../style/Classes.module.less';

export interface ITableContext<T = any> {
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    sortedData: T[];
    setSortedData: Dispatch<SetStateAction<T[]>>;
    sortBy: string | undefined;
    setSortBy: Dispatch<SetStateAction<string | undefined>>;
    reverseSortDirection: boolean;
    setReverseSortDirection: Dispatch<SetStateAction<boolean>>;
    errors: {
        id?: number;
        drugNameError?: string;
        doseError?: string;
        unitIDError?: string;
        doseFrequencyIDError?: string;
        startDateError?: string;
        endDateError?: string;
    }[];
    setErrors: Dispatch<
        SetStateAction<
            {
                id?: number;
                drugNameError?: string;
                doseError?: string;
                unitIDError?: string;
                doseFrequencyIDError?: string;
                startDateError?: string;
                endDateError?: string;
            }[]
        >
    >;
}

const tableContext = React.createContext<ITableContext>({
    search: '',
    setSearch: () => {},
    sortedData: [],
    setSortedData: () => {},
    sortBy: undefined,
    setSortBy: () => {},
    reverseSortDirection: false,
    setReverseSortDirection: () => {},
    errors: [{}],
    setErrors: () => {},
});

type TableContextProviderProps = {
    children: React.ReactNode;
};

export const TableContextProvider: React.FC<TableContextProviderProps> = (props) => {
    const { children } = props;

    const [search, setSearch] = useState<string>('');
    const [sortedData, setSortedData] = useState<any[]>([]); // Modifié pour accepter tout tableau
    const [sortBy, setSortBy] = useState<string | undefined>(undefined);
    const [reverseSortDirection, setReverseSortDirection] = useState(false);
    const [errors, setErrors] = useState([{}]);

    const contextValue: ITableContext = {
        search,
        setSearch,
        sortedData,
        setSortedData,
        sortBy,
        setSortBy,
        reverseSortDirection,
        setReverseSortDirection,
        errors,
        setErrors,
    };

    return <tableContext.Provider value={contextValue}>{children}</tableContext.Provider>;
};

export function useTable() {
    const context: ITableContext = useContext(tableContext);

    const { search, setSearch } = context;
    const { sortedData, setSortedData } = context;
    const { sortBy, setSortBy } = context;
    const { reverseSortDirection, setReverseSortDirection } = context;
    const { errors, setErrors } = context;

    const setSorting = (field: string, data: any[]) => {
        const reversed = field === sortBy ? !reverseSortDirection : false;
        setReverseSortDirection(reversed);
        setSortBy(field);
        setSortedData(tableTools.sortData(data, { sortBy: field, reversed, search }));
    };

    return {
        search,
        setSearch,
        sortedData,
        setSortedData,
        sortBy,
        setSortBy,
        reverseSortDirection,
        setReverseSortDirection,
        setSorting,
        errors,
        setErrors,
    };
}

interface IThProps {
    children: React.ReactNode;
    reversed: boolean;
    sorted: boolean;
    onSort: any;
    width?: number | string;
    visibleFrom?: MantineBreakpoint;
    hiddenFrom?: MantineBreakpoint;
    hidden?: boolean;
}

export const Th = ({
    children,
    reversed,
    sorted,
    onSort,
    width = 'auto',
    visibleFrom = undefined,
    hiddenFrom = undefined,
    hidden = false,
}: IThProps) => {
    const AngleUpDown = () => {
        return (
            <Stack gap={0}>
                <IconChevronUp size={12} />
                <IconChevronDown style={{ marginTop: -5 }} size={12} />
            </Stack>
        );
    };
    const Icon = sorted ? reversed ? <IconChevronUp size={12} /> : <IconChevronDown size={12} /> : <AngleUpDown />;
    return (
        <Table.Th hidden={hidden} w={width} p={0} visibleFrom={visibleFrom} hiddenFrom={hiddenFrom}>
            <Button
                fullWidth
                onClick={onSort}
                variant={'subtle'}
                color={'dark'}
                radius={0}
                rightSection={Icon}
                fw={700}
                className={classes.thButton}
                styles={{
                    root: { paddingInlineStart: theme.spacing?.xs, paddingInlineEnd: theme.spacing?.xs },
                    section: { marginLeft: 'auto' },
                    label: { marginRight: theme.spacing?.xs },
                    inner: { width: width },
                }}
            >
                {children}
            </Button>
        </Table.Th>
    );
};
